import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import {
  AlternateContactSchema,
  useAcceptAlternateContactMutation,
  useMattersProvidersRecordRequestsChangeStatusCreateMutation,
} from '@/services/api/generated/treatmentRecordRequestsService';

interface UseMROutcomeActionsParams {
  matterId: string;
  providerId: string;
  requestId: string;
  taskId: string;
  onActionComplete?: () => void;
}

export function useMROutcomeActions({
  matterId,
  providerId,
  requestId,
  taskId: _taskId, // Reserved for future Voice AI retry integration
  onActionComplete,
}: UseMROutcomeActionsParams) {
  const [selectedAlternateValue, setSelectedAlternateValue] = useState<
    string | null
  >(null);

  const [changeStatus, { isLoading: isChangeStatusLoading }] =
    useMattersProvidersRecordRequestsChangeStatusCreateMutation();
  const [acceptAlternate, { isLoading: isAcceptAlternateLoading }] =
    useAcceptAlternateContactMutation();

  // Mark invoice as paid -> update request status
  const handleMarkInvoicePaid = useCallback(async () => {
    try {
      await changeStatus({
        matterId,
        providerId,
        requestId,
        changeTreatmentRecordRequestStatusSchema: {
          new_status: 'pending_payment',
          reason: 'manual',
          log: {
            details: 'Invoice marked as paid by user',
          },
        },
      }).unwrap();

      toast.success('Marked as paid - awaiting confirmation');
      onActionComplete?.();
    } catch (error) {
      toast.error('Failed to update status');
      console.error('Failed to mark invoice paid:', error);
    }
  }, [matterId, providerId, requestId, changeStatus, onActionComplete]);

  // Use alternate contact -> accept alternate and restart workflow
  const handleUseAlternate = useCallback(
    async (alternate: AlternateContactSchema) => {
      try {
        setSelectedAlternateValue(alternate.value);

        await acceptAlternate({
          matterId,
          providerId,
          requestId,
          acceptAlternateContactSchema: {
            handle_type: alternate.handle_type,
            value: alternate.value,
            label: alternate.label,
            confidence: alternate.confidence,
          },
        }).unwrap();

        toast.success(
          `Switched to ${alternate.handle_type}: ${alternate.value}`,
        );
        onActionComplete?.();
      } catch (error) {
        toast.error('Failed to switch contact');
        console.error('Failed to use alternate contact:', error);
      } finally {
        setSelectedAlternateValue(null);
      }
    },
    [matterId, providerId, requestId, acceptAlternate, onActionComplete],
  );

  // Retry now -> trigger a new Voice AI call (would need Bland call service)
  const handleRetryNow = useCallback(async () => {
    // This would trigger a new Voice AI workflow
    // For now, we'll show a placeholder toast
    toast.info('Manual retry - please use Voice AI controls to start a call');
  }, []);

  // Escalate -> send to BPO queue
  const handleEscalate = useCallback(async () => {
    // This would trigger escalation to BPO
    // For now, we'll show a placeholder toast
    toast.info('Escalation requested - BPO integration coming soon');
  }, []);

  return {
    handleMarkInvoicePaid,
    handleUseAlternate,
    handleRetryNow,
    handleEscalate,
    isMarkPaidLoading: isChangeStatusLoading,
    isUseAlternateLoading: isAcceptAlternateLoading,
    selectedAlternateValue,
    // Retry and escalate don't have loading states yet
    isRetryLoading: false,
    isEscalateLoading: false,
  };
}
